/**
 * Sampling Policy for App Insights telemetry
 * Story: 116-123 - Instrument API and error telemetry
 *
 * Implements the following sampling rules:
 *   - Request / Dependency: 20% sampling
 *   - Exception: 100% (always capture)
 *   - client_error events: 100% (always capture)
 *   - Sev1 severity override: 100% (always capture)
 */

/**
 * Telemetry item base-type labels as used by the App Insights SDK envelope.
 */
export type TelemetryItemType =
  | 'RequestData'
  | 'RemoteDependencyData'
  | 'ExceptionData'
  | 'EventData'
  | 'MessageData'
  | 'PageViewData'
  | string

/**
 * Sampling rates (0–1) keyed by App Insights base-type.
 * Items not listed here default to 100% sampling (rate = 1.0).
 */
export const SAMPLING_RATES: Readonly<Record<string, number>> = {
  RequestData: 0.2,
  RemoteDependencyData: 0.2,
  ExceptionData: 1.0,
  EventData: 1.0,
  MessageData: 1.0,
  PageViewData: 1.0,
}

/** Modulus used to normalise the djb2 hash to a float in [0, 1). */
const HASH_MODULUS = 100_000

/**
 * Derive a deterministic float in [0, 1) from a correlation ID.
 * Uses a djb2-style hash so that sampling decisions are stable for the same
 * session/request chain and correlated items are consistently in- or out-sampled.
 *
 * @param correlationId - A UUID v4 correlation ID string
 * @returns A float in [0, 1)
 */
export function hashCorrelationId(correlationId: string): number {
  let hash = 0
  for (let i = 0; i < correlationId.length; i++) {
    // djb2: (hash << 5) - hash equals hash * 31, then add char code
    hash = ((hash << 5) - hash + correlationId.charCodeAt(i)) | 0
  }
  return (Math.abs(hash) % HASH_MODULUS) / HASH_MODULUS
}

/**
 * Determine whether a telemetry item should be sampled in.
 *
 * Priority (highest to lowest):
 *  1. Sev1 override → always sample in
 *  2. client_error event → always sample in
 *  3. ExceptionData → always sample in
 *  4. Rate for itemType (defaults to 1.0 if unrecognised)
 *
 * For deterministic sampling across a session, pass the result of
 * `hashCorrelationId(sessionCorrelationId)` as the `random` argument so that
 * related telemetry items share the same sampling decision.
 *
 * @param itemType     - The App Insights base-type string (e.g. 'RequestData')
 * @param isSev1       - Whether this item carries Sev1 severity (always captured)
 * @param isClientError - Whether this is a client_error custom event (always captured)
 * @param random       - A value in [0, 1) used for rate comparison; defaults to Math.random()
 * @returns true if the item should be sent; false if it should be dropped
 */
export function shouldSampleIn(
  itemType: TelemetryItemType,
  isSev1 = false,
  isClientError = false,
  random: number = Math.random()
): boolean {
  // Priority 1 – Sev1 override: always capture
  if (isSev1) return true

  // Priority 2 – client_error events: always capture
  if (isClientError) return true

  // Priority 3 – Exceptions: always capture
  if (itemType === 'ExceptionData') return true

  // Priority 4 – Apply per-type rate
  const rate = SAMPLING_RATES[itemType] ?? 1.0
  return random < rate
}
